import { Box, Circle, Flex, Image } from "@chakra-ui/react"
import React from "react"
import { useCarouselSizeThumnail } from "./hooks/useCarouselSizeThumnail"
import {
  IndicatorConfig,
  useCarouselStyleIndicator,
} from "./hooks/useCarouselStyleIndicator"

interface ImageProps {
  path: string
  alt?: string
}

interface Props {
  displayableImages: ImageProps[]
  currentIndex: number
  selectImage: (index: number) => void
  indicatorConfig: Record<string, IndicatorConfig>
}

interface IProps {
  indicatorConfig: Record<string, IndicatorConfig>
  image: ImageProps
  currentIndex: number
  index: number
  selectImage: (index: number) => void
  thumbnailWidth: number
  thumbnailHeight: number
}

interface DotIndicatorProps {
  index: number
  currentIndex: number
  selectImage: (index: number) => void
  indicatorColor: string
  indicatorFocusColor: string
}

interface ThumbnailIndicatorProps {
  selectImage: (index: number) => void
  index: number
  currentIndex: number
  image: ImageProps
  indicatorColor: string
  indicatorFocusColor: string
  thumbnailWidth: number
  thumbnailHeight: number
}

const CarouselIndicator: React.FC<Props> = props => {
  const { displayableImages, currentIndex, selectImage, indicatorConfig } =
    props

  const { thumbnailWidth, thumbnailHeight } = useCarouselSizeThumnail()

  return (
    <Flex
      mt={4}
      aria-label="indicator-wrapper"
      p={0}
      justifyContent={"flex-start"}
      className="s__indicator-wrapper"
    >
      {displayableImages.map((image, index) => (
        <Indicator
          key={`indicator-${index}`}
          indicatorConfig={indicatorConfig}
          image={image}
          currentIndex={currentIndex}
          index={index}
          selectImage={selectImage}
          thumbnailWidth={thumbnailWidth}
          thumbnailHeight={thumbnailHeight}
        />
      ))}
    </Flex>
  )
}

export default CarouselIndicator

export const Indicator: React.FC<IProps> = ({
  currentIndex,
  index,
  selectImage,
  image,
  indicatorConfig,
  thumbnailWidth,
  thumbnailHeight,
}) => {
  const { indicatorType, indicatorColor, indicatorFocusColor } =
    useCarouselStyleIndicator(indicatorConfig)

  if (indicatorType === "dot") {
    return (
      <DotIndicator
        index={index}
        currentIndex={currentIndex}
        selectImage={selectImage}
        indicatorColor={indicatorColor}
        indicatorFocusColor={indicatorFocusColor}
      />
    )
  }

  return (
    <ThumbnailIndicator
      selectImage={selectImage}
      index={index}
      currentIndex={currentIndex}
      image={image}
      indicatorColor={indicatorColor}
      indicatorFocusColor={indicatorFocusColor}
      thumbnailWidth={thumbnailWidth}
      thumbnailHeight={thumbnailHeight}
    />
  )
}

export const DotIndicator = ({
  index,
  currentIndex,
  selectImage,
  indicatorColor,
  indicatorFocusColor,
}: DotIndicatorProps) => {
  const indicatorSizeXs = "10px"
  return (
    <Circle
      aria-label="point indicator"
      key={`circle-${index}`}
      sx={{
        marginInlineStart: 1,
        marginInlineEnd: 1,
      }}
      className="s__indicator-circle"
      bgColor={currentIndex === index ? indicatorFocusColor : indicatorColor}
      cursor="pointer"
      size={indicatorSizeXs}
      onClick={() => selectImage(index)}
    />
  )
}

export const ThumbnailIndicator = ({
  selectImage,
  index,
  currentIndex,
  image,
  indicatorColor,
  indicatorFocusColor,
  thumbnailWidth,
  thumbnailHeight,
}: ThumbnailIndicatorProps) => {
  const indicatorSize = 120
  const thumbnailIndicatorSize = {
    base: "55px",
    md: `${indicatorSize * 0.8}px`,
    xl: `${indicatorSize}px`,
  }

  const thumbnailStyle = {
    borderRadius: 5,
    borderColor: indicatorColor,
  }

  return (
    <Box
      id={`image-grid-indicator-${index}`}
      className="s__indicator-image"
      aria-label="indicator-image"
      cursor="pointer"
      overflow="hidden"
      borderWidth={"2px"}
      mr={1}
      mb={1}
      onClick={() => selectImage(index)}
      {...thumbnailStyle}
      borderColor={
        currentIndex === index ? indicatorFocusColor : indicatorColor
      }
      maxHeight={thumbnailIndicatorSize}
      width={thumbnailIndicatorSize}
      aspectRatio={1}
    >
      <Image
        className="s__indicator-thumbnail"
        src={image.path}
        alt={image.alt || "indicator-thumbnail"}
        objectFit="cover"
        htmlWidth={thumbnailWidth}
        htmlHeight={thumbnailHeight}
        width="full"
        height={"full"}
      />
    </Box>
  )
}
